/**
 * imagePrep — Image file preparation utilities
 *
 * Philosophie : on **upload toujours** le fichier dans Storage. Pas de
 * conversion HEIC client-side, pas de pre-thumb genere. Le rendu est gere
 * cote display via Supabase Storage `render/image/...?format=jpeg&width=...`
 * qui passe par ImgProxy serveur (sait decoder HEIC, JPEG, WebP, PNG).
 *
 * Trade-off vs l'ancienne approche (heic2any + convert-heic edge fn) :
 *   - Plus de "format not supported" / HTTP 546 / WORKER_LIMIT.
 *   - Plus de 1.3 MB de JS dans le bundle.
 *   - Bytes uploads peuvent etre plus gros (originaux vs JPEG resize), mais
 *     le client n'attend plus 2-3s de conversion JS/edge avant l'upload.
 *   - Si une photo iPhone fait 5 MB en HEIC, on l'envoie comme ca dans le
 *     bucket. Storage transforms genere les previews a la volee.
 *
 * On tente toujours un resize CLIENT pour les images >1.5 MB qu'on sait
 * decoder (JPEG/PNG/WebP) — economie de bande passante upload. Si le decode
 * echoue (HEIC sur Chrome, JPEG non standard, etc.) on upload tel quel.
 */

const MAX_DIMENSION = 1920
const QUALITY = 0.85
const RESIZE_THRESHOLD_BYTES = 1.5 * 1024 * 1024

export interface PrepareImageResult {
  /** The processed file, ready to upload. JPEG resize si possible, sinon original. */
  file: File
  /** True iff a HEIC→JPEG conversion happened (toujours false avec la nouvelle approche). */
  convertedFromHeic: boolean
  /** True iff a resize happened. */
  resized: boolean
}

/** True iff the file is a HEIC / HEIF image, by MIME type or extension. */
export function isHeic(file: File): boolean {
  const type = (file.type || '').toLowerCase()
  if (type === 'image/heic' || type === 'image/heif') return true
  return /\.(heic|heif)$/i.test(file.name)
}

/** Decode a File into an HTMLImageElement. Resolve `null` au lieu de throw
 *  pour permettre un fallback "upload as-is".
 *  Defensif : si on est dans un env sans Image (ex: vitest node), on resolve
 *  null silencieusement plutot que de crasher. */
function tryDecodeToImage(file: File): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof Image === 'undefined' || typeof URL === 'undefined') {
      return resolve(null)
    }
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}

/** Resize an image to fit within `maxDim` px on the long side, return JPEG Blob. */
function resizeToJpeg(img: HTMLImageElement, maxDim: number, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    const ratio = Math.min(1, maxDim / Math.max(img.width, img.height))
    const w = Math.max(1, Math.round(img.width * ratio))
    const h = Math.max(1, Math.round(img.height * ratio))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return resolve(null)
    ctx.drawImage(img, 0, 0, w, h)
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
  })
}

/**
 * Prepare an image file for upload :
 *
 *   - Si <1.5 MB → renvoie le fichier tel quel (pas la peine de resize).
 *   - Sinon, tente un decode + resize 1920px JPEG. Si le decode echoue
 *     (HEIC sur Chrome, JPEG corrompu, format exotique) → renvoie le
 *     fichier original. Le rendu cote display via Supabase Storage
 *     render/image se chargera de generer un JPEG visible.
 *
 *  Ne throw JAMAIS — toujours possible d'uploader (au pire le file original).
 */
export async function prepareImageFile(file: File): Promise<PrepareImageResult> {
  // Fichier petit ou HEIC : pas de resize cote client. Upload tel quel.
  if (file.size <= RESIZE_THRESHOLD_BYTES || isHeic(file)) {
    return { file, convertedFromHeic: false, resized: false }
  }

  // Tentative de decode + resize. Si fail (image format non supporte
  // par le browser, fichier corrompu, etc.) → upload tel quel.
  const img = await tryDecodeToImage(file)
  if (!img) {
    return { file, convertedFromHeic: false, resized: false }
  }
  const blob = await resizeToJpeg(img, MAX_DIMENSION, QUALITY)
  if (!blob) {
    return { file, convertedFromHeic: false, resized: false }
  }
  const baseName = file.name.replace(/\.(jpe?g|png|webp)$/i, '') || 'photo'
  const out = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
  return { file: out, convertedFromHeic: false, resized: true }
}
